import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/lib/api-client";

export interface AnnouncementResponse {
  id: string;
  title: string;
  body_md: string;
  cta_label: string | null;
  cta_url: string | null;
  created_at: string;
  updated_at: string;
}

export interface AnnouncementHistoryItem extends AnnouncementResponse {
  is_read: boolean;
  acked_at: string | null;
}

export function useUnreadAnnouncements() {
  return useQuery<AnnouncementResponse[]>({
    queryKey: ["announcements", "unread"],
    queryFn: () => apiClient.get("api/announcements/unread").json(),
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });
}

export function useAnnouncementHistory() {
  return useQuery<AnnouncementHistoryItem[]>({
    queryKey: ["announcements", "history"],
    queryFn: () => apiClient.get("api/announcements/history").json(),
    staleTime: 60_000,
  });
}

export function useAckAnnouncement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiClient.post(`api/announcements/${id}/ack`).then(() => undefined),
    onMutate: async (id: string) => {
      await qc.cancelQueries({ queryKey: ["announcements"] });
      const prevUnread = qc.getQueryData<AnnouncementResponse[]>([
        "announcements",
        "unread",
      ]);
      const prevHistory = qc.getQueryData<AnnouncementHistoryItem[]>([
        "announcements",
        "history",
      ]);
      qc.setQueryData<AnnouncementResponse[]>(
        ["announcements", "unread"],
        (old) => (old ?? []).filter((a) => a.id !== id),
      );
      qc.setQueryData<AnnouncementHistoryItem[]>(
        ["announcements", "history"],
        (old) =>
          (old ?? []).map((a) =>
            a.id === id
              ? { ...a, is_read: true, acked_at: new Date().toISOString() }
              : a,
          ),
      );
      return { prevUnread, prevHistory };
    },
    onError: (_e, _id, ctx) => {
      if (ctx?.prevUnread)
        qc.setQueryData(["announcements", "unread"], ctx.prevUnread);
      if (ctx?.prevHistory)
        qc.setQueryData(["announcements", "history"], ctx.prevHistory);
    },
    onSettled: () =>
      qc.invalidateQueries({ queryKey: ["announcements"] }),
  });
}
